import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { analytics } from '../services/analytics';
import PlaylistCard from '../components/PlaylistCard';
import LoadingSpinner from '../components/LoadingSpinner';

function PlaylistList() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    analytics.pageView('playlist_list');
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated) {
      loadPlaylists();
    }
  }, [auth.isAuthenticated]);

  const loadPlaylists = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.getPlaylists();
      setPlaylists(data.playlists || []);
    } catch (err) {
      setError(err.message);
      analytics.error('load_playlists_failed', { message: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setIsDeleting(true);
      await api.deletePlaylist(deleteConfirm.id);
      analytics.playlistDeleted(deleteConfirm.id);
      setPlaylists(prev => prev.filter(p => p.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.message);
      analytics.error('delete_playlist_failed', { message: err.message });
    } finally {
      setIsDeleting(false);
    }
  };

  if (auth.isLoading) {
    return <LoadingSpinner message="Authenticating..." />;
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="page-container">
        <div className="not-authenticated">
          <svg className="not-authenticated-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h2>Access Required</h2>
          <p>
            Please scan the QR code displayed on your TV to manage your playlists.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading playlists..." />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>My Playlists</h1>
        <button className="btn btn-primary" onClick={() => navigate('/add')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Playlist
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {playlists.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
          <h2>No Playlists Yet</h2>
          <p>Add your first playlist to get started</p>
          <button className="btn btn-primary" onClick={() => navigate('/add')}>
            Add Playlist
          </button>
        </div>
      ) : (
        <div className="playlists-grid">
          {playlists.map(playlist => (
            <PlaylistCard
              key={playlist.id}
              playlist={playlist}
              onDelete={setDeleteConfirm}
            />
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <h3>Delete Playlist?</h3>
            <p>
              Are you sure you want to delete "{deleteConfirm.name}"? This action cannot be undone.
            </p>
            <div className="confirm-dialog-buttons">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlaylistList;
